import React, { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Input,
  InputNumber,
  Select,
  Switch,
  Button,
  Space,
  Divider,
  Typography,
  Row,
  Col,
  Alert,
  Tooltip,
  Card,
  Tag,
} from 'antd';
import {
  InfoCircleOutlined,
  WarningOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { AdminQuest, ActionRequirement, CreateQuestPayload } from '../lib/api';

// ---------------------------------------------------------------------------
// Circular prerequisite detection
// ---------------------------------------------------------------------------

function detectCircularPrerequisites(
  candidateId: string,
  candidatePrereqs: string[],
  allQuests: AdminQuest[]
): string[][] {
  const graph: Map<string, string[]> = new Map();
  for (const q of allQuests) {
    if (q.quest_id === candidateId) {
      graph.set(candidateId, candidatePrereqs);
    } else {
      graph.set(q.quest_id, q.prerequisite_quests || []);
    }
  }
  if (!graph.has(candidateId)) {
    graph.set(candidateId, candidatePrereqs);
  }

  const cycles: string[][] = [];
  const visited = new Set<string>();
  const recStack: string[] = [];

  function dfs(node: string): boolean {
    visited.add(node);
    recStack.push(node);
    for (const dep of graph.get(node) || []) {
      if (!visited.has(dep)) {
        if (dfs(dep)) return true;
      } else if (recStack.includes(dep)) {
        const cycleStart = recStack.indexOf(dep);
        cycles.push([...recStack.slice(cycleStart), dep]);
        return true;
      }
    }
    recStack.pop();
    return false;
  }

  dfs(candidateId);
  return cycles;
}

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface QuestFormProps {
  initialValues?: AdminQuest;
  onSubmit: (values: CreateQuestPayload) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  existingQuests?: AdminQuest[];
}

const QUEST_TYPES = [
  { value: 'action', label: 'Action (triggered once by a specific action)' },
  { value: 'count', label: 'Count (requires N actions to complete)' },
  { value: 'visit', label: 'Visit (triggered by visiting a page)' },
];

const ACTION_TYPES = [
  { value: 'add_item', label: 'Add Item to Closet' },
  { value: 'try_on', label: 'Virtual Try-On' },
  { value: 'chat', label: 'Chat with AI Stylist' },
  { value: 'get_recommendation', label: 'Get Outfit Recommendation' },
  { value: 'upload_model', label: 'Upload / Generate Photo Model' },
  { value: 'batch_add', label: 'Batch Add Items' },
  { value: 'visit_page', label: 'Visit a Page' },
  { value: 'complete_profile', label: 'Complete Profile' },
  { value: 'complete_survey', label: 'Complete Survey' },
];

const REWARD_ACTIONS = [
  { value: 'generate_photo_models', label: 'Generate AI Photo Models' },
];

const SUBSCRIPTION_TIERS = [
  { value: 'free', label: 'Free' },
  { value: 'bronze', label: 'Bronze' },
  { value: 'silver', label: 'Silver' },
  { value: 'gold', label: 'Gold' },
  { value: 'firooze', label: 'Firooze' },
];

const CTA_ACTIONS = [
  { value: 'none', label: 'None (just dismiss)' },
  { value: 'redirect', label: 'Redirect to a page' },
  { value: 'tour', label: 'Launch a Slide Tour' },
];

const ICONS = [
  { value: 'star', label: '⭐ Star' },
  { value: 'trophy', label: '🏆 Trophy' },
  { value: 'gem', label: '💎 Gem' },
  { value: 'fire', label: '🔥 Fire' },
  { value: 'rocket', label: '🚀 Rocket' },
  { value: 'shirt', label: '👕 Shirt' },
  { value: 'camera', label: '📷 Camera' },
  { value: 'chat', label: '💬 Chat' },
  { value: 'sparkles', label: '✨ Sparkles' },
  { value: 'crown', label: '👑 Crown' },
  { value: 'check', label: '✅ Check' },
  { value: 'target', label: '🎯 Target' },
  { value: 'user-circle', label: '👤 User' },
  { value: 'layers', label: '📦 Layers' },
  { value: 'wand-2', label: '🪄 Magic Wand' },
];

// ---------------------------------------------------------------------------
// Compound Quest Row Editor
// ---------------------------------------------------------------------------

interface ActionReqRow extends ActionRequirement {
  _key: number; // local list key
}

let _keyCounter = 0;

const ActionRequirementsEditor: React.FC<{
  value: ActionReqRow[];
  onChange: (rows: ActionReqRow[]) => void;
}> = ({ value, onChange }) => {
  const addRow = () => {
    onChange([...value, { _key: ++_keyCounter, action_type: '', target_count: 1 }]);
  };

  const removeRow = (key: number) => {
    onChange(value.filter((r) => r._key !== key));
  };

  const updateRow = (key: number, patch: Partial<ActionRequirement>) => {
    onChange(value.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  };

  return (
    <div>
      {value.map((row, idx) => (
        <Row key={row._key} gutter={8} style={{ marginBottom: 8 }} align="middle">
          <Col flex="auto">
            <Select
              style={{ width: '100%' }}
              placeholder="Action type"
              value={row.action_type || undefined}
              onChange={(v) => updateRow(row._key, { action_type: v })}
            >
              {ACTION_TYPES.map((a) => (
                <Option key={a.value} value={a.value}>
                  {a.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col style={{ width: 140 }}>
            <InputNumber
              min={1}
              max={10000}
              value={row.target_count}
              onChange={(v) => updateRow(row._key, { target_count: v ?? 1 })}
              style={{ width: '100%' }}
              addonBefore="×"
              placeholder="Count"
            />
          </Col>
          <Col>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeRow(row._key)}
              disabled={value.length <= 1}
            />
          </Col>
        </Row>
      ))}
      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        onClick={addRow}
        style={{ marginTop: 4 }}
      >
        Add Action Requirement
      </Button>
      {value.length > 0 && (
        <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 4 }}>
          All requirements must be met for the quest to complete.
        </Text>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// QuestForm
// ---------------------------------------------------------------------------

const QuestForm: React.FC<QuestFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
  isLoading = false,
  existingQuests = [],
}) => {
  const [form] = Form.useForm();
  const questType = Form.useWatch('quest_type', form);
  const watchedPrereqs = Form.useWatch('prerequisite_quests', form) as string[] | undefined;
  const watchedQuestId = Form.useWatch('quest_id', form) as string | undefined;
  const claimBottomsheetEnabled = Form.useWatch('claim_bottomsheet_enabled', form);
  const ctaAction = Form.useWatch('claim_bottomsheet_cta_action', form);

  // Compound quest state (managed outside Ant Form to keep it simple)
  const isCompound = !!(
    initialValues?.action_requirements && initialValues.action_requirements.length > 0
  );
  const [useCompound, setUseCompound] = useState(isCompound);
  const [actionReqs, setActionReqs] = useState<ActionReqRow[]>(() => {
    if (initialValues?.action_requirements?.length) {
      return initialValues.action_requirements.map((r) => ({ ...r, _key: ++_keyCounter }));
    }
    return [{ _key: ++_keyCounter, action_type: '', target_count: 1 }];
  });

  // Real-time circular dependency detection
  const circularCycles = useMemo(() => {
    const candidateId = watchedQuestId || initialValues?.quest_id || '__new__';
    const prereqs = watchedPrereqs || [];
    if (prereqs.length === 0) return [];
    return detectCircularPrerequisites(candidateId, prereqs, existingQuests);
  }, [watchedQuestId, watchedPrereqs, existingQuests, initialValues?.quest_id]);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        quest_id: initialValues.quest_id,
        title: initialValues.title,
        description: initialValues.description,
        quest_type: initialValues.quest_type,
        target_count: initialValues.target_count,
        action_type: initialValues.action_type,
        reward_zar_credits: initialValues.reward_zar_credits,
        reward_free_subscription_days: initialValues.reward_free_subscription_days,
        reward_subscription_tier: initialValues.reward_subscription_tier,
        reward_actions: initialValues.reward_actions || [],
        prerequisite_quests: initialValues.prerequisite_quests || [],
        is_onboarding: initialValues.is_onboarding ?? true,
        order: initialValues.order,
        is_active: initialValues.is_active ?? true,
        icon: initialValues.icon,
        hint: initialValues.hint,
        help_url: initialValues.help_url,
        help_image_url: initialValues.help_image_url,
        help_video_key: initialValues.help_video_key ?? null,
        claim_bottomsheet_enabled: initialValues.claim_bottomsheet_enabled ?? false,
        claim_bottomsheet_title: initialValues.claim_bottomsheet_title,
        claim_bottomsheet_description: initialValues.claim_bottomsheet_description,
        claim_bottomsheet_image_url: initialValues.claim_bottomsheet_image_url,
        claim_bottomsheet_cta_label: initialValues.claim_bottomsheet_cta_label,
        claim_bottomsheet_cta_action: initialValues.claim_bottomsheet_cta_action,
        claim_bottomsheet_cta_url: initialValues.claim_bottomsheet_cta_url,
        claim_bottomsheet_cta_tour_id: initialValues.claim_bottomsheet_cta_tour_id,
        claim_message: initialValues.claim_message ?? null,
        celebration_message: initialValues.celebration_message ?? null,
      });
    } else {
      form.setFieldsValue({
        is_onboarding: true,
        is_active: true,
        target_count: 1,
        prerequisite_quests: [],
        reward_actions: [],
        claim_bottomsheet_enabled: false,
      });
    }
  }, [initialValues, form]);

  const handleSubmit = async (values: any) => {
    if (circularCycles.length > 0) return;

    const payload: CreateQuestPayload = {
      quest_id: values.quest_id,
      title: values.title,
      description: values.description,
      quest_type: values.quest_type,
      target_count: useCompound ? 1 : values.target_count,
      prerequisite_quests: values.prerequisite_quests || [],
      is_onboarding: values.is_onboarding,
      order: values.order,
      is_active: values.is_active,
      icon: values.icon,
      hint: values.hint,
      help_url: values.help_url,
      help_image_url: values.help_image_url,
      help_video_key: values.help_video_key || null,
      reward_zar_credits: values.reward_zar_credits,
      reward_free_subscription_days: values.reward_free_subscription_days,
      reward_subscription_tier: values.reward_subscription_tier,
      reward_actions: values.reward_actions || [],
      // Claim bottomsheet
      claim_bottomsheet_enabled: values.claim_bottomsheet_enabled ?? false,
      claim_bottomsheet_title: values.claim_bottomsheet_title || null,
      claim_bottomsheet_description: values.claim_bottomsheet_description || null,
      claim_bottomsheet_image_url: values.claim_bottomsheet_image_url || null,
      claim_bottomsheet_cta_label: values.claim_bottomsheet_cta_label || null,
      claim_bottomsheet_cta_action: values.claim_bottomsheet_cta_action || null,
      claim_bottomsheet_cta_url: values.claim_bottomsheet_cta_url || null,
      claim_bottomsheet_cta_tour_id: values.claim_bottomsheet_cta_tour_id || null,
      claim_message: values.claim_message || null,
      celebration_message: values.celebration_message || null,
    };

    if (useCompound) {
      const validReqs = actionReqs.filter((r) => r.action_type);
      if (validReqs.length === 0) {
        form.setFields([{ name: '_actionReqs', errors: ['Add at least one action requirement'] }]);
        return;
      }
      payload.action_requirements = validReqs.map(({ action_type, target_count }) => ({
        action_type,
        target_count,
      }));
      payload.action_type = null;
    } else {
      payload.action_type = values.action_type;
      payload.action_requirements = [];
    }

    await onSubmit(payload);
  };

  const isEditing = !!initialValues;
  const otherQuests = existingQuests.filter(
    (q) => !isEditing || q.quest_id !== initialValues?.quest_id
  );

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
      requiredMark="optional"
    >
      <Title level={5} style={{ marginBottom: 16 }}>
        {isEditing ? 'Edit Quest' : 'Create New Quest'}
      </Title>

      {/* ── Basic Information ── */}
      <Divider orientation="left">Basic Information</Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="quest_id"
            label="Quest ID"
            rules={[
              { required: true, message: 'Quest ID is required' },
              { pattern: /^[a-z0-9_]+$/, message: 'Only lowercase letters, numbers, and underscores' },
            ]}
            extra="Unique identifier (e.g., first_item, try_on_3)"
          >
            <Input placeholder="e.g., first_try_on" disabled={isEditing} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="order"
            label="Display Order"
            rules={[{ required: true, message: 'Order is required' }]}
            extra="Lower numbers appear first"
          >
            <InputNumber min={0} max={1000} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="title"
        label="Title"
        rules={[{ required: true, message: 'Title is required' }]}
      >
        <Input placeholder="e.g., Add Your First Item" maxLength={100} />
      </Form.Item>

      <Form.Item
        name="description"
        label="Description"
        rules={[{ required: true, message: 'Description is required' }]}
      >
        <TextArea placeholder="Explain what the user needs to do..." maxLength={500} rows={3} showCount />
      </Form.Item>

      <Form.Item
        name="hint"
        label="Hint (optional)"
        extra="A small tip shown to help the user complete the quest"
      >
        <Input placeholder="e.g., Tap the + button on the home screen" maxLength={200} />
      </Form.Item>

      {/* ── Quest Logic ── */}
      <Divider orientation="left">Quest Logic</Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name="quest_type"
            label="Quest Type"
            rules={[{ required: true, message: 'Quest type is required' }]}
          >
            <Select placeholder="Select type">
              {QUEST_TYPES.map((t) => (
                <Option key={t.value} value={t.value}>{t.label}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label={
              <Space>
                Compound Quest (multiple actions)
                <Tooltip title="When enabled, the quest requires the user to complete multiple different actions before it's marked as done. Mutually exclusive with a single Action Type.">
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </Space>
            }
            style={{ marginBottom: 0 }}
          >
            <Switch
              checked={useCompound}
              onChange={(v) => setUseCompound(v)}
              checkedChildren="Compound"
              unCheckedChildren="Single"
            />
          </Form.Item>
        </Col>
      </Row>

      {/* Single-action mode */}
      {!useCompound && (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="action_type"
              label="Action Type"
              rules={[{ required: true, message: 'Action type is required for single-action quests' }]}
            >
              <Select placeholder="Select action">
                {ACTION_TYPES.map((a) => (
                  <Option key={a.value} value={a.value}>{a.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="target_count"
              label={questType === 'count' ? 'Target Count' : 'Target Count (always 1 for action/visit)'}
              rules={[{ required: true, message: 'Target count is required' }]}
            >
              <InputNumber
                min={1}
                max={10000}
                style={{ width: '100%' }}
                disabled={questType !== 'count'}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* Compound-action mode */}
      {useCompound && (
        <Card
          size="small"
          style={{ marginBottom: 16, background: '#f9f0ff', borderColor: '#d3adf7' }}
          title={
            <Space>
              <Tag color="purple">Compound Quest</Tag>
              <Text style={{ fontWeight: 'normal', fontSize: 12 }}>
                All requirements below must be met to complete this quest
              </Text>
            </Space>
          }
        >
          <ActionRequirementsEditor value={actionReqs} onChange={setActionReqs} />
          {/* Hidden field used for error display */}
          <Form.Item name="_actionReqs" style={{ marginBottom: 0 }}>
            <input type="hidden" />
          </Form.Item>
        </Card>
      )}

      {/* Prerequisites */}
      <Form.Item
        name="prerequisite_quests"
        label={
          <Space>
            Prerequisite Quests (optional)
            <Tooltip title="Users must complete these quests before this one unlocks. Circular dependencies are automatically detected.">
              <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </Space>
        }
        extra="Users must complete these quests before this one unlocks"
        validateStatus={circularCycles.length > 0 ? 'error' : undefined}
      >
        <Select mode="multiple" placeholder="Select prerequisite quests" allowClear optionFilterProp="children">
          {otherQuests.map((q) => (
            <Option key={q.quest_id} value={q.quest_id}>
              [{q.order}] {q.title} ({q.quest_id})
            </Option>
          ))}
        </Select>
      </Form.Item>

      {circularCycles.length > 0 && (
        <Alert
          type="error"
          icon={<WarningOutlined />}
          showIcon
          message="Circular Dependency Detected"
          description={
            <div>
              <p>The selected prerequisites create a circular chain.</p>
              {circularCycles.map((cycle, i) => (
                <p key={i} style={{ fontFamily: 'monospace', fontSize: 12, margin: '4px 0' }}>
                  🔄 {cycle.join(' → ')}
                </p>
              ))}
            </div>
          }
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ── Rewards ── */}
      <Divider orientation="left">Rewards (all optional)</Divider>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        You can combine any or all reward types.
      </Text>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item name="reward_zar_credits" label="ZAR Credits" extra="Amount of ZAR credits">
            <InputNumber min={0} max={100000} style={{ width: '100%' }} placeholder="e.g., 50" addonBefore="ZAR" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="reward_free_subscription_days"
            label="Free Subscription Days"
            extra={<>Set with a tier → temporary reward (e.g. 7 days Gold). Leave tier empty → temporary Bronze.</>}
          >
            <InputNumber min={0} max={365} style={{ width: '100%' }} placeholder="e.g., 7" addonAfter="days" />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="reward_subscription_tier"
            label="Subscription Tier Upgrade"
            extra={<><strong>With days</strong> → temporary. <strong>Without days</strong> → permanent.</>}
          >
            <Select allowClear placeholder="No tier reward">
              {SUBSCRIPTION_TIERS.map((t) => (
                <Option key={t.value} value={t.value}>{t.label}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        name="reward_actions"
        label={
          <Space>
            Side-Effect Actions on Completion
            <Tooltip title="Automated actions triggered when the quest is completed, in addition to the reward fields above.">
              <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
            </Tooltip>
          </Space>
        }
        extra="e.g. auto-generate AI photo models when the user completes their profile"
      >
        <Select mode="multiple" placeholder="None" allowClear>
          {REWARD_ACTIONS.map((a) => (
            <Option key={a.value} value={a.value}>{a.label}</Option>
          ))}
        </Select>
      </Form.Item>

      {/* ── Celebration & Claim messages ── */}
      <Divider orientation="left">Celebration & Claim messages</Divider>
      <Form.Item
        name="celebration_message"
        label="Celebration message (optional)"
        extra="Shown on the celebration bottomsheet when the quest is completed (before the user claims). Leave empty to use the quest title."
      >
        <Input placeholder="e.g., You did it! Claim your 30 ZAR now." maxLength={200} allowClear />
      </Form.Item>

      {/* ── Claim Bottomsheet ── */}
      <Divider orientation="left">
        <Space>
          Claim Bottomsheet
          <Tooltip title="Shown when the user clicks Claim on the quest page (after completing the quest). The admin controls all content and the CTA button action.">
            <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
          </Tooltip>
        </Space>
      </Divider>

      <Form.Item
        name="claim_bottomsheet_enabled"
        label="Enable Custom Claim Bottomsheet"
        valuePropName="checked"
        extra="Off = simple modal with claim message below. On = custom bottomsheet (title, description, CTA) when user claims."
      >
        <Switch checkedChildren="Enabled" unCheckedChildren="Disabled" />
      </Form.Item>

      {/* Claim message: only when claim bottomsheet is OFF */}
      {!claimBottomsheetEnabled && (
        <Form.Item
          name="claim_message"
          label="Claim message (optional)"
          extra="Shown in the simple success modal when the user claims (only used when custom claim bottomsheet is disabled)."
        >
          <Input placeholder="e.g., You've earned 7 days of Gold!" maxLength={200} allowClear />
        </Form.Item>
      )}

      {claimBottomsheetEnabled && (
        <Card size="small" style={{ marginBottom: 16, background: '#fff7e6', borderColor: '#ffd591' }}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="claim_bottomsheet_title" label="Title" extra="e.g. 🎉 Gold Plan Unlocked!">
                <Input placeholder="Headline shown on the bottomsheet" maxLength={100} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="claim_bottomsheet_image_url" label="Hero Image URL (optional)">
                <Input placeholder="https://..." />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="claim_bottomsheet_description" label="Description">
            <TextArea
              placeholder="Body text explaining the reward..."
              rows={2}
              maxLength={300}
              showCount
            />
          </Form.Item>

          <Divider style={{ margin: '8px 0' }}>CTA Button</Divider>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="claim_bottomsheet_cta_label" label="Button Label" extra='e.g. "Explore Gold Features"'>
                <Input placeholder="e.g., Explore Gold Features" maxLength={60} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="claim_bottomsheet_cta_action" label="Button Action">
                <Select allowClear placeholder="None (dismiss only)">
                  {CTA_ACTIONS.map((a) => (
                    <Option key={a.value} value={a.value}>{a.label}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              {ctaAction === 'redirect' && (
                <Form.Item name="claim_bottomsheet_cta_url" label="Redirect URL" extra='e.g. "/subscription"'>
                  <Input placeholder="/subscription" />
                </Form.Item>
              )}
              {ctaAction === 'tour' && (
                <Form.Item name="claim_bottomsheet_cta_tour_id" label="Slide Tour ID" extra="Must be an existing slide-type tour_id">
                  <Input placeholder="e.g., gold_features_tour" />
                </Form.Item>
              )}
            </Col>
          </Row>
        </Card>
      )}

      {/* ── UI & Settings ── */}
      <Divider orientation="left">UI & Settings</Divider>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="icon" label="Icon" extra="Icon identifier for UI display">
            <Select allowClear placeholder="Select icon">
              {ICONS.map((i) => (
                <Option key={i.value} value={i.value}>{i.label}</Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="help_url" label="Help URL (optional)" extra="Deep link to the feature this quest is about">
            <Input placeholder="e.g., /closet or /ai-stylist" />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item name="help_image_url" label="Help Image URL (optional)">
        <Input placeholder="https://..." />
      </Form.Item>

      <Form.Item
        name="help_video_key"
        label="Tutorial Illustration (optional)"
        extra="When set, tapping the quest card opens an animated how-to illustration. The CTA button navigates to the Help URL above."
      >
        <Select allowClear placeholder="No illustration — navigate directly">
          <Option value="smart_add">📱 Smart Add Clothes</Option>
          {/* Add new illustration keys here as they are built */}
        </Select>
      </Form.Item>

      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="is_onboarding" label="Onboarding Quest" valuePropName="checked">
            <Switch checkedChildren="Yes" unCheckedChildren="No" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
        </Col>
      </Row>

      <Divider />

      <Form.Item>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            disabled={circularCycles.length > 0}
          >
            {isEditing ? 'Update Quest' : 'Create Quest'}
          </Button>
          <Button onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
        </Space>
        {circularCycles.length > 0 && (
          <div style={{ marginTop: 8, color: '#ff4d4f', fontSize: 12 }}>
            ⚠️ Resolve circular dependencies before saving.
          </div>
        )}
      </Form.Item>
    </Form>
  );
};

export default QuestForm;
